import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    TextField,
    MenuItem, Box, Autocomplete, Checkbox, ListItemText,
} from '@mui/material';
import { PhoneInput } from 'react-international-phone';
// The literal build/ path, not the package's "style.css" export subpath — this
// project's webpack 4 doesn't understand package.json "exports" maps.
import 'react-international-phone/build/index.css';
import { getCountries, getPhoneCountries, getStatesByCountry, useGeo } from '../../../utils/geo';
import {
    LANGUAGES_SUPPORTED,
    PRIMARY_ACTIVITY_CODE, PRIMARY_ACTIVITY_CODE_VERBOSE,
    ADDITIONAL_CAPABILITY_CODE, ADDITIONAL_CAPABILITY_CODE_VERBOSE,
    ORGANIZATION_TYPE_CODE, ORGANIZATION_TYPE_CODE_VERBOSE,
} from "../../../utils/constants";
import {isValidEmail, getPhoneDialCode, splitPhoneByDialCode} from "../../../utils/utils";
import {listOrgSchoolsService} from "../../../services/client.service";
import FormNotice from "./FormNotice.comp";
import {isAccountPublished} from "./AccountLifecycleBadge.comp";

const ORANGE = 'var(--brand-primary, #FF6900)';

const defaultFields = {
    org_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_title: '',
    contact_email: '',
    phone_number: '',
    preferred_locale: '',
    org_address: '',
    org_city: '',
    postal_code: '',
    org_country: '',
    // Network-only — "Schema for Data Collection" §2.
    org_phone_country_code: '',
    org_phone_number: '',
    org_state: '',
    org_website: '',
    // Network address, second line — see CreateClientDialog.
    org_address_line2: '',
    primary_activities: [],
    additional_capabilities: [],
    // Single-school-only — "Schema for Data Collection" §3, stored on the
    // school's OrgInstitute record.
    number_of_learners: '',
    institute_phone_country_code: '',
    institute_phone_number: '',
    institute_type: '',
    institute_primary_activities: [],
    institute_address_line1: '',
    institute_address_line2: '',
    institute_state: '',
    institute_website: '',
    institute_additional_capabilities: [],
};

/**
 * Split a legacy single-string contact_name into first/last.
 *
 * Rows created before the split fields existed only have `contact_name`. Opening
 * the dialog on one of those must not present two empty required boxes, so the
 * old value is split on the first space — first token first name, remainder last
 * name. Rows created since carry the real fields and never reach this.
 */
const splitLegacyName = (client) => {
    if (client.contact_first_name || client.contact_last_name) {
        return {
            contact_first_name: client.contact_first_name || '',
            contact_last_name: client.contact_last_name || '',
        };
    }
    const parts = String(client.contact_name || '').trim().split(/\s+/).filter(Boolean);
    return {
        contact_first_name: parts[0] || '',
        contact_last_name: parts.slice(1).join(' '),
    };
};

/** Pure validators — one copy, driving both the messages and the Save button. */
const validateAccount = (fields, isNetwork) => {
    const e = {};
    if (!fields.org_name) e.org_name = 'Required';
    if (!fields.contact_first_name) e.contact_first_name = 'Required';
    if (!fields.contact_last_name) e.contact_last_name = 'Required';
    if (!fields.contact_email) e.contact_email = 'Required';
    else if (!isValidEmail(fields.contact_email)) e.contact_email = 'Invalid email address';
    // A single school hides the combined address field in favour of the split
    // Address Line 1 / Line 2 pair, so Line 1 carries the requirement instead.
    if (!isNetwork) {
        if (!fields.institute_address_line1) e.institute_address_line1 = 'Required';
    } else if (!fields.org_address) e.org_address = 'Required';
    if (!fields.org_city) e.org_city = 'Required';
    if (!fields.postal_code) e.postal_code = 'Required';
    if (!fields.org_country) e.org_country = 'Required';
    return e;
};

/** Same, for one school inside a network. */
const validateSchool = (sc) => {
    const e = {};
    if (!sc.org_name) e.org_name = 'Required';
    // The combined address field is hidden on the school card too — Line 1
    // carries the requirement instead.
    if (!sc.institute_address_line1) e.institute_address_line1 = 'Required';
    if (!sc.org_city) e.org_city = 'Required';
    if (!sc.postal_code) e.postal_code = 'Required';
    if (!sc.org_country) e.org_country = 'Required';
    if (!sc.contact_first_name) e.contact_first_name = 'Required';
    if (!sc.contact_last_name) e.contact_last_name = 'Required';
    if (!sc.contact_email) e.contact_email = 'Required';
    else if (!isValidEmail(sc.contact_email)) e.contact_email = 'Invalid email address';
    return e;
};

const toSchoolForm = (sc) => ({
    org_code: sc.org_code,
    // Which school of the network this is. A network's schools share its
    // org_code, so the save must name the school or it lands on the network.
    institute_code: sc.institute_code || '',
    org_name: sc.org_name || '',
    org_address: sc.org_address || '',
    org_city: sc.org_city || '',
    postal_code: sc.postal_code || '',
    org_country: sc.country || '',
    ...splitLegacyName(sc),
    contact_title: sc.contact_title || '',
    contact_email: sc.contact_email || '',
    lifecycle_status: sc.lifecycle_status,
    // OrgInstitute fields — "Schema for Data Collection" §3.
    number_of_learners: sc.number_of_learners ?? '',
    institute_phone_country_code: sc.institute_phone_country_code || sc.org_phone_country_code || '',
    institute_phone_number: sc.institute_phone_number || sc.org_phone_number || '',
    institute_type: sc.institute_type || '',
    institute_primary_activities: sc.institute_primary_activities || [],
    institute_address_line1: sc.institute_address_line1 || sc.org_address || '',
    institute_address_line2: sc.institute_address_line2 || sc.org_address_line2 || '',
    institute_state: sc.institute_state || sc.org_state || '',
    institute_website: sc.institute_website || sc.org_website || '',
    institute_additional_capabilities: sc.institute_additional_capabilities || [],
});

/**
 * Edit a school, or a school network together with the schools already in it.
 *
 *
 * PW deck slides 39 (school), 40-41 (network). Two things the annotations settle:
 *  - slide 39/40: "Hide preferred language for now" — the field stays in the
 *    form state so an existing value survives a save, but is not rendered.
 *  - slide 41: "Sub-schools can't be either removed or added in this editing
 *    pop-up" — hence no +/x controls on the school cards, and the notice at the
 *    bottom pointing at "+ Add a school" on the network row instead.
 */
/*
 * A school's own fields (phone, type, address lines, state, website, ...) live
 * on its OrgInstitute row, which GET /dqin/client/list does not carry. They are
 * read from /client/org/sections/list when the dialog opens and merged in, so
 * the form opens with what is stored — otherwise it opened blank and Save wrote
 * the blanks over the stored values.
 */
const INSTITUTE_KEYS = [
    'number_of_learners', 'institute_phone_country_code', 'institute_phone_number',
    'institute_type', 'institute_primary_activities', 'institute_address_line1',
    'institute_address_line2', 'institute_state', 'institute_website',
    'institute_additional_capabilities',
];
const instituteFieldsOf = (row) => (row
    ? Object.fromEntries(INSTITUTE_KEYS.filter((k) => row[k] !== undefined && row[k] !== null).map((k) => [k, row[k]]))
    : {});

/** The client row with each school's institute fields merged in. */
const withInstituteFields = async (row) => {
    const codes = [...new Set([row.org_code, ...(row.schools || []).map((sc) => sc.org_code)].filter(Boolean))];
    const lists = await Promise.all(codes.map((code) => listOrgSchoolsService(code).catch(() => [])));
    const byPair = {};
    const byOrg = {};
    codes.forEach((code, i) => {
        byOrg[code] = lists[i] || [];
        (lists[i] || []).forEach((r) => { byPair[`${code}::${r.institute_code}`] = r; });
    });
    // A single school: its own row — named by its institute_code inside a
    // network, or the one row under its own org_code when it stands alone.
    const ownRow = row.is_school_network ? null
        : (row.institute_code ? byPair[`${row.org_code}::${row.institute_code}`] : (byOrg[row.org_code] || [])[0]);
    return {
        ...row,
        ...instituteFieldsOf(ownRow),
        schools: (row.schools || []).map((sc) => ({
            ...sc,
            ...instituteFieldsOf(byPair[`${sc.org_code}::${sc.institute_code}`]),
        })),
    };
};


const EditClientDialog = ({ open, onClose, onSubmit, loading, client: clientRow, viewerIsNetwork = false }) => {
    useGeo(); // re-render with the API's lists once they load
    const [enriched, setEnriched] = useState(null);
    const [instituteLoading, setInstituteLoading] = useState(false);
    // Everything below reads `client` — the row, then the row with its schools'
    // institute fields once they have loaded.
    const client = enriched || clientRow;

    useEffect(() => {
        setEnriched(null);
        if (!open || !clientRow) return undefined;
        let cancelled = false;
        setInstituteLoading(true);
        withInstituteFields(clientRow)
            .then((merged) => { if (!cancelled) setEnriched(merged); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setInstituteLoading(false); });
        return () => { cancelled = true; };
    }, [clientRow, open]);

    const [fields, setFields] = useState(defaultFields);
    const [schools, setSchools] = useState([]);
    const [touched, setTouched] = useState({});
    const [schoolTouched, setSchoolTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    /* Slide 39: changing the contact email needs an extra confirmation. */
    const [pendingOwnershipChange, setPendingOwnershipChange] = useState(null);

    const isNetwork = !!(client && client.is_school_network);
    /*
     * Seed for the phone input, read from the stable `client` prop rather than
     * `fields` state — `fields` changes on every keystroke, and re-feeding that
     * back into PhoneInput's `value` on every render fights its internal cursor
     * tracking (see the comment beside the PhoneInput below). `client` only
     * changes when the dialog is opened for a different record, so this string
     * stays constant while the user types.
     */
    const initialPhoneValue = client && (client.org_phone_country_code || client.institute_phone_country_code)
        ? `${client.org_phone_country_code || client.institute_phone_country_code} ${client.org_phone_number || client.institute_phone_number || ''}`.trim()
        : '';
    /** Same idea, for the school's own institute phone number. */
    const initialInstitutePhoneValue = client && (client.institute_phone_country_code || client.org_phone_country_code)
        ? `${client.institute_phone_country_code || client.org_phone_country_code} ${client.institute_phone_number || client.org_phone_number || ''}`.trim()
        : '';

    useEffect(() => {
        if (!open) return;
        setTouched({});
        setSchoolTouched({});
        setSubmitAttempted(false);
        setPendingOwnershipChange(null);
        if (!client) {
            setFields(defaultFields);
            setSchools([]);
            return;
        }
        setFields({
            org_name: client.org_name || '',
            ...splitLegacyName(client),
            contact_title: client.contact_title || '',
            contact_email: client.contact_email || '',
            phone_number: client.phone_number || '',
            preferred_locale: client.preferred_locale || '',
            org_address: client.org_address || '',
            org_city: client.org_city || '',
            postal_code: client.postal_code || '',
            org_country: client.country || '',
            org_phone_country_code: client.org_phone_country_code || client.institute_phone_country_code || '',
            org_phone_number: client.org_phone_number || client.institute_phone_number || '',
            org_state: client.org_state || client.institute_state || '',
            org_website: client.org_website || client.institute_website || '',
            org_address_line2: client.org_address_line2 || client.institute_address_line2 || '',
            primary_activities: client.primary_activities || [],
            additional_capabilities: client.additional_capabilities || [],
            number_of_learners: client.number_of_learners ?? '',
            institute_phone_country_code: client.institute_phone_country_code || client.org_phone_country_code || '',
            institute_phone_number: client.institute_phone_number || client.org_phone_number || '',
            institute_type: client.institute_type || '',
            institute_primary_activities: client.institute_primary_activities || [],
            institute_address_line1: client.institute_address_line1 || client.org_address || '',
            institute_address_line2: client.institute_address_line2 || client.org_address_line2 || '',
            institute_state: client.institute_state || client.org_state || '',
            institute_website: client.institute_website || client.org_website || '',
            institute_additional_capabilities: client.institute_additional_capabilities || [],
        });
        setSchools((client.schools || []).map(toSchoolForm));
    }, [client, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields(f => ({ ...f, [name]: value }));
        setTouched((t) => ({ ...t, [name]: true }));
    };

    const updateSchool = (idx, key, value) => {
        setSchools((list) => list.map((sc, i) => (i === idx ? { ...sc, [key]: value } : sc)));
        setSchoolTouched((t) => ({ ...t, [idx]: { ...(t[idx] || {}), [key]: true } }));
    };

    const handleClose = () => {
        setFields(defaultFields);
        setSchools([]);
        setTouched({});
        setSchoolTouched({});
        setSubmitAttempted(false);
        onClose();
    };

    // Validated on every keystroke. `errors` / `schoolErrors` hold only what is
    // currently visible; `isFormValid` is the whole picture and gates Save.
    const liveErrors = validateAccount(fields, isNetwork);
    const liveSchoolErrors = schools.map(validateSchool);
    const isFormValid =
        Object.keys(liveErrors).length === 0 &&
        liveSchoolErrors.every((e) => Object.keys(e).length === 0);

    const errors = Object.fromEntries(
        Object.entries(liveErrors).filter(([k]) => submitAttempted || touched[k]),
    );
    const schoolErrors = liveSchoolErrors.map((e, idx) => Object.fromEntries(
        Object.entries(e).filter(([k]) => submitAttempted || (schoolTouched[idx] || {})[k]),
    ));

    /** The address the workspace currently belongs to. */
    const originalEmail = (client?.contact_email || '').trim().toLowerCase();
    const nextEmail = (fields.contact_email || '').trim().toLowerCase();
    const emailIsChanging = !!originalEmail && !!nextEmail && originalEmail !== nextEmail;

    const buildPayload = () => {
        const payload = {
            ...fields,
            contact_name: `${fields.contact_first_name} ${fields.contact_last_name}`.trim(),
            // A school inside a network shares the network's org_code; this is
            // what makes the save land on the school, not the network.
            institute_code: client?.institute_code || undefined,
            /*
             * Network-only — "Schema for Data Collection" §2. undefined rather than
             * omitted, same as callback_url/callback_key below: editClient drops
             * undefined keys, so a single school's edit never touches these.
             */
            ...(isNetwork ? {} : {
                // When editing a single school / school client, synchronize phone, website,
                // state, and address onto the Organization record as well.
                org_phone_country_code: fields.institute_phone_country_code || fields.org_phone_country_code || undefined,
                org_phone_number: fields.institute_phone_number || fields.org_phone_number || undefined,
                org_state: fields.institute_state || fields.org_state || undefined,
                org_website: fields.institute_website || fields.org_website || undefined,
                org_address: fields.institute_address_line1 || fields.org_address || undefined,
                org_address_line2: fields.institute_address_line2 || fields.org_address_line2 || undefined,
                primary_activities: undefined,
                additional_capabilities: undefined,
            }),
            /*
             * Single-school-only — "Schema for Data Collection" §3, stored on the
             * OrgInstitute record. undefined (not omitted) for a network's own
             * edit, same reasoning as the network-only fields above.
             * institute_head_title / institute_address mirror the account-level
             * contact_title / org_address the form already collects, rather than
             * showing the same value in a second input.
             */
            ...(isNetwork ? {
                number_of_learners: undefined,
                institute_phone_country_code: undefined,
                institute_phone_number: undefined,
                institute_head_title: undefined,
                institute_type: undefined,
                institute_primary_activities: undefined,
                institute_address: undefined,
                institute_address_line1: undefined,
                institute_address_line2: undefined,
                institute_state: undefined,
                institute_website: undefined,
                institute_additional_capabilities: undefined,
            } : {
                number_of_learners: fields.number_of_learners === '' ? null : Number(fields.number_of_learners),
                institute_phone_country_code: fields.institute_phone_country_code || fields.org_phone_country_code || undefined,
                institute_phone_number: fields.institute_phone_number || fields.org_phone_number || undefined,
                institute_head_title: fields.contact_title,
                institute_state: fields.institute_state || fields.org_state || undefined,
                institute_website: fields.institute_website || fields.org_website || undefined,
                // org_address is hidden for a single school — Line 1 is the real
                // address now, so it feeds institute_address instead.
                institute_address: fields.institute_address_line1,
            }),
        };
        /*
         * callback_url / callback_key are deliberately NOT sent.
         *
         * The API-access control they belong to is not on slides 39-41, so it was
         * removed. Sending them as empty strings would clear credentials an org
         * already has; omitting them leaves the stored values untouched, because
         * editClient drops undefined keys.
         */
        return payload;
    };

    const commit = () => {
        onSubmit(buildPayload(), schools.map((sc) => ({
            ...sc,
            contact_name: `${sc.contact_first_name} ${sc.contact_last_name}`.trim(),
            number_of_learners: sc.number_of_learners === '' ? null : Number(sc.number_of_learners),
            institute_head_title: sc.contact_title,
            // Mirror phone, website, state, and address onto organization fields
            // so both Organization and OrgInstitute have them populated.
            org_phone_country_code: sc.institute_phone_country_code || undefined,
            org_phone_number: sc.institute_phone_number || undefined,
            org_state: sc.institute_state || undefined,
            org_website: sc.institute_website || undefined,
            org_address: sc.institute_address_line1 || sc.org_address || undefined,
            org_address_line2: sc.institute_address_line2 || undefined,
            // org_address is hidden on the school card too — Line 1 is the
            // real address now, so it feeds institute_address.
            institute_address: sc.institute_address_line1,
        })));
    };

    /**
     * Slide 39: "Even the ownership can be edited, but extra confirmation step
     * needs to be designed. (If email is going to be changed.)"
     *
     * The pack never drew that step, so this is the plainest thing that meets
     * the requirement: a save that moves the workspace to a different address
     * has to be confirmed, and the confirmation names both addresses so the
     * change cannot happen by accident. The copy is ours — flag it if the
     * design lands differently.
     */
    const handleSave = () => {
        setSubmitAttempted(true);
        if (!isFormValid) return;
        if (emailIsChanging) {
            setPendingOwnershipChange({ from: client?.contact_email, to: fields.contact_email });
            return;
        }
        commit();
    };



    const schoolWord = schools.length === 1 ? 'school' : 'schools';
    const countryCount = new Set(schools.map((sc) => sc.org_country).filter(Boolean)).size;
    const countryWord = countryCount === 1 ? 'country' : 'countries';

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '15px' } }}>
            <DialogTitle sx={{ textAlign: 'center', fontSize: '24px', fontWeight: 'bold', pb: 0.5 }}>
                {isNetwork ? 'Edit School Network Information' : 'Edit School Information'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ textAlign: 'center', fontSize: 15, mb: 2, color: 'var(--brand-text-muted, #6B7280)' }}>
                    {/* Ian-PW-12Oct-01 / Ian-SNW, SW-12Oct-01 — one line for a school, whoever edits it. */}
                    {isNetwork
                        ? "Update this network's details and save your changes."
                        : "Update this school's details and save your changes."}
                </Typography>

                <TextField
                    label={isNetwork ? 'Network Name' : 'School Name'}
                    name="org_name"
                    value={fields.org_name}
                    onChange={handleChange}
                    error={!!errors.org_name}
                    helperText={errors.org_name}
                    fullWidth
                    sx={{ mb: 2 }}
                    autoFocus
                />

                {/* Contact block — boxed, exactly as the mock-up frames it. */}
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '10px', p: 2, mb: 2,
                }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                        {isNetwork ? 'Network contact' : 'Contact person'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="First Name" name="contact_first_name"
                            value={fields.contact_first_name} onChange={handleChange}
                            error={!!errors.contact_first_name} helperText={errors.contact_first_name}
                            fullWidth
                        />
                        <TextField
                            label="Last Name" name="contact_last_name"
                            value={fields.contact_last_name} onChange={handleChange}
                            error={!!errors.contact_last_name} helperText={errors.contact_last_name}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Title / Role (optional)" name="contact_title"
                            value={fields.contact_title} onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Contact Email" name="contact_email" type="email"
                            value={fields.contact_email} onChange={handleChange}
                            error={!!errors.contact_email}
                            helperText={
                                isAccountPublished(client?.lifecycle_status)
                                    ? 'Contact email cannot be changed once the workspace is published.'
                                    : errors.contact_email
                            }
                            disabled={isAccountPublished(client?.lifecycle_status)}
                            fullWidth
                        />
                    </Box>
                    {/*
                      * Network's phone number is one combined box — dial code shown
                      * inline, typed or picked from the flag dropdown, same as the
                      * library's own demo. "Schema for Data Collection" §2 still wants
                      * the dial code and number as two API fields, so onChange splits
                      * the combined value back apart. A single school keeps the plain
                      * field it always had.
                      */}
                    {isNetwork ? (
                        <Box>
                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mb: '6px' }}>
                                Network Phone Number
                            </Typography>
                            <PhoneInput
                                countries={getPhoneCountries()}
                                key={`${client?.org_code || 'edit-phone'}-${enriched ? 'loaded' : 'row'}`}
                                defaultCountry="us"
                                placeholder="Phone Number"
                                // Without this, the library re-inserts the current country's
                                // dial code the instant the field is cleared to empty — the
                                // field could never actually go blank to be retyped from scratch.
                                disableDialCodePrefill
                                /*
                                 * `value` is only a seed here, sourced from the stable `client`
                                 * prop — never from `fields`, which changes on every keystroke.
                                 * Feeding the library's own onChange output straight back in as
                                 * `value` fought its internal cursor tracking: each render reset
                                 * the caret to just after "+", so the next digit landed at the
                                 * START of the number instead of the end, scrambling it and
                                 * re-triggering country guessing on every keystroke. The `key`
                                 * above forces a remount (fresh seed) when a different client is
                                 * opened, since this component instance is otherwise reused.
                                 */
                                value={initialPhoneValue}
                                // The library's default dropdown z-index (1) loses to sibling
                                // fields painted after it in the same MUI Dialog stacking
                                // context, so it renders half-hidden behind them. Lift it above
                                // the dialog's own content instead of guessing at a global override.
                                countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                onChange={(phone, countryIso2) => {
                                    if (!phone) {
                                        setFields((f) => ({ ...f, org_phone_country_code: '', org_phone_number: '' }));
                                        return;
                                    }
                                    const dialCode = getPhoneDialCode(countryIso2);
                                    setFields((f) => ({
                                        ...f,
                                        org_phone_country_code: dialCode,
                                        org_phone_number: splitPhoneByDialCode(phone, dialCode),
                                    }));
                                }}
                                inputProps={{ name: 'org_phone_number', autoComplete: 'tel' }}
                                inputStyle={{ flex: 1, minWidth: 0 }}
                                style={{
                                    width: '100%',
                                    '--react-international-phone-height': '56px',
                                    '--react-international-phone-font-size': '16px',
                                    '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                }}
                            />
                        </Box>
                    ) : (
                        <Box>
                            <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mb: '6px' }}>
                                School Phone Number
                            </Typography>
                            <PhoneInput
                                countries={getPhoneCountries()}
                                key={`${client?.org_code || 'edit-institute-phone'}-${enriched ? 'loaded' : 'row'}`}
                                defaultCountry="us"
                                placeholder="Phone Number"
                                disableDialCodePrefill
                                value={initialInstitutePhoneValue}
                                countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                onChange={(phone, countryIso2) => {
                                    if (!phone) {
                                        setFields((f) => ({ ...f, institute_phone_country_code: '', institute_phone_number: '' }));
                                        return;
                                    }
                                    const dialCode = getPhoneDialCode(countryIso2);
                                    setFields((f) => ({
                                        ...f,
                                        institute_phone_country_code: dialCode,
                                        institute_phone_number: splitPhoneByDialCode(phone, dialCode),
                                    }));
                                }}
                                inputProps={{ name: 'institute_phone_number', autoComplete: 'tel' }}
                                inputStyle={{ flex: 1, minWidth: 0 }}
                                style={{
                                    width: '100%',
                                    '--react-international-phone-height': '56px',
                                    '--react-international-phone-font-size': '16px',
                                    '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                }}
                            />
                        </Box>
                    )}
                </Box>

                {/*
                  * Network-only fields — "Schema for Data Collection" §2: state,
                  * website, and the two pick-lists. Recorded only;
                  * additional_capabilities does not change pricing, access or
                  * billing.
                  */}
                {isNetwork && (
                    <>
                        <TextField
                            label="Network Website (optional)"
                            name="org_website"
                            value={fields.org_website}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            autoComplete="url"
                        />
                        {/* Primary Role / Activity hidden for a network per request. */}
                        {/*
                        <TextField
                            select
                            label="Primary Role / Activity"
                            name="primary_activities"
                            value={fields.primary_activities}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => selected
                                    .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                    .join(', '),
                            }}
                        >
                            {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                <MenuItem key={code} value={code}>
                                    <Checkbox checked={fields.primary_activities.indexOf(code) > -1} />
                                    <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                </MenuItem>
                            ))}
                        </TextField>
                        */}
                        {/* Additional Capabilities hidden for a network per request. */}
                        {/*
                        <TextField
                            select
                            label="Additional Capabilities"
                            name="additional_capabilities"
                            value={fields.additional_capabilities}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => selected
                                    .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                    .join(', '),
                            }}
                        >
                            {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                <MenuItem key={code} value={code}>
                                    <Checkbox checked={fields.additional_capabilities.indexOf(code) > -1} />
                                    <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                </MenuItem>
                            ))}
                        </TextField>
                        */}
                    </>
                )}

                {/*
                  * Single-school-only fields — "Schema for Data Collection" §3,
                  * stored on the OrgInstitute record. This dialog only edits a
                  * network's account or an individual school, never a generic
                  * non-school client, so `!isNetwork` here means "single school".
                  */}
                {!isNetwork && (
                    <>
                        {/*
                          * Number of Learners and School Organization Type are
                          * hidden for a single school per request.
                          */}
                        {/*
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Number of Learners (informational)"
                                name="number_of_learners"
                                type="number"
                                value={fields.number_of_learners}
                                onChange={handleChange}
                                fullWidth
                                helperText="Not the licence pool — seats are still assigned separately."
                            />
                            <TextField
                                select
                                label="School Organization Type"
                                name="institute_type"
                                value={fields.institute_type}
                                onChange={handleChange}
                                fullWidth
                            >
                                {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                                    <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        */}
                        {/* School Primary Role / Activity hidden for a single school per request. */}
                        {/*
                        <TextField
                            select
                            label="School Primary Role / Activity"
                            name="institute_primary_activities"
                            value={fields.institute_primary_activities}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => selected
                                    .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                    .join(', '),
                            }}
                        >
                            {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                <MenuItem key={code} value={code}>
                                    <Checkbox checked={fields.institute_primary_activities.indexOf(code) > -1} />
                                    <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                </MenuItem>
                            ))}
                        </TextField>
                        */}
                        <TextField
                            label="School Website (optional)"
                            name="institute_website"
                            value={fields.institute_website}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            autoComplete="url"
                        />
                        {/* Additional Capabilities hidden for a single school per request. */}
                        {/*
                        <TextField
                            select
                            label="Additional Capabilities"
                            name="institute_additional_capabilities"
                            value={fields.institute_additional_capabilities}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => selected
                                    .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                    .join(', '),
                            }}
                        >
                            {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                <MenuItem key={code} value={code}>
                                    <Checkbox checked={fields.institute_additional_capabilities.indexOf(code) > -1} />
                                    <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                </MenuItem>
                            ))}
                        </TextField>
                        */}
                    </>
                )}

                {/*
                  * Preferred Language was hidden per the "Hide preferred
                  * language for now" annotation on slides 39/40, shown again
                  * per a later request, and commented out again for now.
                  */}
                {/*
                <TextField
                    select
                    label="Preferred Language"
                    name="preferred_locale"
                    value={fields.preferred_locale}
                    onChange={handleChange}
                    fullWidth
                    sx={{ mb: 2 }}
                >
                    {Object.entries(LANGUAGES_SUPPORTED).map(([code, name]) => (
                        <MenuItem key={code} value={code}>{name}</MenuItem>
                    ))}
                </TextField>
                */}

                {/*
                  * Combined address field — kept for the network's own Address.
                  * A single school hides this in favour of the split Address
                  * Line 1 / Line 2 pair below, with Line 1 required instead —
                  * see validateAccount.
                  */}
                {/* A network's own address is Line 1 (org_address, required) +
                    Line 2 (optional), matching the school forms — Ian's review comment. */}
                {isNetwork && (
                    <>
                        <TextField
                            label="Network Address — Line 1"
                            name="org_address"
                            value={fields.org_address}
                            onChange={handleChange}
                            error={!!errors.org_address}
                            helperText={errors.org_address}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        {/* Network address line 2 — stored on the Organization as org_address_line2. */}
                        <TextField
                            label="Network Address — Line 2 (optional)"
                            name="org_address_line2"
                            value={fields.org_address_line2}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    </>
                )}
                {/*
                  * Address line 1/2 sit right under the main address field so
                  * every address-related input reads as one group.
                  */}
                {!isNetwork && (
                    <>
                        <TextField
                            label="School Address — Line 1"
                            name="institute_address_line1"
                            value={fields.institute_address_line1}
                            onChange={handleChange}
                            error={!!errors.institute_address_line1}
                            helperText={errors.institute_address_line1}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="School Address — Line 2 (optional)"
                            name="institute_address_line2"
                            value={fields.institute_address_line2}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    </>
                )}
                <Autocomplete
                    options={getCountries()}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                    value={getCountries().find((option) => option.code === fields.org_country) || null}
                    onChange={(event, newValue) => {
                        setFields((f) => ({
                            ...f,
                            org_country: newValue?.code || '',
                            // The state list is keyed by country — a stale code from the
                            // previous country would silently point at the wrong state.
                            org_state: '',
                            institute_state: '',
                        }));
                        setTouched((t) => ({ ...t, org_country: true }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Country"
                            variant="outlined"
                            error={!!errors.org_country}
                            helperText={errors.org_country}
                        />
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                {/*
                  * State/province, below Country since its options depend on which
                  * country is picked — same cascading Autocomplete pattern as the
                  * per-student state picker in AddClassStudentsDialog.jsx, backed by
                  * the same ISO 3166-2 states (the API's geo master, via utils/geo.js).
                  */}
                {(() => {
                    const stateKey = isNetwork ? 'org_state' : 'institute_state';
                    const statesForCountry = getStatesByCountry(fields.org_country);
                    return (
                        <Autocomplete
                            options={statesForCountry}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            value={statesForCountry.find((s) => s.code === fields[stateKey]) || null}
                            onChange={(event, newValue) => {
                                setFields((f) => ({ ...f, [stateKey]: newValue ? newValue.code : '' }));
                            }}
                            disabled={!fields.org_country || statesForCountry.length === 0}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={isNetwork ? 'Network State / Province / Region' : 'School State / Province / Region'}
                                    variant="outlined"
                                    placeholder={
                                        !fields.org_country
                                            ? 'Select country first'
                                            : statesForCountry.length === 0 ? 'N/A' : 'Select state'
                                    }
                                />
                            )}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    );
                })()}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        label="City" name="org_city"
                        value={fields.org_city} onChange={handleChange}
                        error={!!errors.org_city} helperText={errors.org_city}
                        fullWidth
                    />
                    <TextField
                        label="Postal Code" name="postal_code"
                        value={fields.postal_code} onChange={handleChange}
                        error={!!errors.postal_code} helperText={errors.postal_code}
                        fullWidth
                    />
                </Box>

                {/* Slides 40-41: the network's schools, editable in place. */}
                {isNetwork && schools.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                Schools in this network
                            </Typography>
                            <Typography sx={{
                                fontSize: 12, color: 'var(--brand-text-muted, #6B7280)',
                                border: '1px solid var(--brand-border, #E5E7EB)',
                                borderRadius: '999px', px: 1.25, py: '2px',
                            }}>
                                {schools.length} {schoolWord} in {countryCount} {countryWord}
                            </Typography>
                        </Box>

                        {schools.map((sc, idx) => (
                            <Box key={sc.org_code || idx} sx={{
                                border: '1px solid var(--brand-border, #E5E7EB)',
                                borderRadius: '10px', p: 2, mb: 2,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <Box sx={{
                                        width: 20, height: 20, borderRadius: '6px',
                                        backgroundColor: 'rgba(255,105,0,0.12)', color: '#FF6900',
                                        fontSize: 12, fontWeight: 700,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{idx + 1}</Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>School {idx + 1}</Typography>
                                </Box>

                                {/*
                                  * Field order mirrors the individual school form above:
                                  * Name → Contact → Phone → Learners/Type → Activity →
                                  * Website → Capabilities → Address → Country/State/City/Postal.
                                  * (institute_head_title / institute_address are mirrored from
                                  * contact_title / org_address at submit time — see commit().)
                                  */}
                                <TextField
                                    label="School Name"
                                    value={sc.org_name}
                                    onChange={(e) => updateSchool(idx, 'org_name', e.target.value)}
                                    error={!!(schoolErrors[idx] || {}).org_name}
                                    helperText={(schoolErrors[idx] || {}).org_name}
                                    fullWidth sx={{ mb: 2 }}
                                />

                                <Typography sx={{
                                    fontSize: 12, fontWeight: 700, letterSpacing: '0.4px',
                                    color: 'var(--brand-text-muted, #6B7280)', mb: 1,
                                }}>
                                    SCHOOL CONTACT PERSON
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        label="First Name"
                                        value={sc.contact_first_name}
                                        onChange={(e) => updateSchool(idx, 'contact_first_name', e.target.value)}
                                        error={!!(schoolErrors[idx] || {}).contact_first_name}
                                        helperText={(schoolErrors[idx] || {}).contact_first_name}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Last Name"
                                        value={sc.contact_last_name}
                                        onChange={(e) => updateSchool(idx, 'contact_last_name', e.target.value)}
                                        error={!!(schoolErrors[idx] || {}).contact_last_name}
                                        helperText={(schoolErrors[idx] || {}).contact_last_name}
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        label="Title / Role (optional)"
                                        value={sc.contact_title}
                                        onChange={(e) => updateSchool(idx, 'contact_title', e.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Email"
                                        type="email"
                                        value={sc.contact_email}
                                        onChange={(e) => updateSchool(idx, 'contact_email', e.target.value)}
                                        error={!!(schoolErrors[idx] || {}).contact_email}
                                        helperText={
                                            isAccountPublished(sc.lifecycle_status)
                                                ? 'Email cannot be changed once published.'
                                                : (schoolErrors[idx] || {}).contact_email
                                        }
                                        disabled={isAccountPublished(sc.lifecycle_status)}
                                        fullWidth
                                    />
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 13, color: 'var(--brand-text-muted, #6B7280)', mb: '6px' }}>
                                        School Phone Number
                                    </Typography>
                                    <PhoneInput
                                        countries={getPhoneCountries()}
                                        key={`${sc.institute_code || sc.org_code || idx}-${enriched ? 'loaded' : 'row'}`}
                                        defaultCountry="us"
                                        placeholder="Phone Number"
                                        disableDialCodePrefill
                                        /*
                                         * Seeded from the ORIGINAL client.schools[idx], not from
                                         * `sc` (the live, editable copy in `schools` state) — `sc`
                                         * changes on every keystroke, which reproduces the same
                                         * cursor-jump bug the top-level phone inputs had.
                                         */
                                        value={(() => {
                                            const orig = (client?.schools || [])[idx];
                                            return orig && orig.institute_phone_country_code
                                                ? `${orig.institute_phone_country_code} ${orig.institute_phone_number || ''}`.trim()
                                                : '';
                                        })()}
                                        countrySelectorStyleProps={{ dropdownStyleProps: { style: { zIndex: 1500 } } }}
                                        onChange={(phone, countryIso2) => {
                                            if (!phone) {
                                                updateSchool(idx, 'institute_phone_country_code', '');
                                                updateSchool(idx, 'institute_phone_number', '');
                                                return;
                                            }
                                            const dialCode = getPhoneDialCode(countryIso2);
                                            updateSchool(idx, 'institute_phone_country_code', dialCode);
                                            updateSchool(idx, 'institute_phone_number', splitPhoneByDialCode(phone, dialCode));
                                        }}
                                        inputProps={{ name: `institute_phone_number_${idx}`, autoComplete: 'tel' }}
                                        inputStyle={{ flex: 1, minWidth: 0 }}
                                        style={{
                                            width: '100%',
                                            '--react-international-phone-height': '56px',
                                            '--react-international-phone-font-size': '16px',
                                            '--react-international-phone-border-color': 'rgba(0, 0, 0, 0.23)',
                                        }}
                                    />
                                </Box>
                                {/*
                                  * Number of Learners and School Organization Type
                                  * are hidden for a network's school card too, same
                                  * as the single-school form.
                                  */}
                                {/*
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        label="Number of Learners (informational)"
                                        type="number"
                                        value={sc.number_of_learners}
                                        onChange={(e) => updateSchool(idx, 'number_of_learners', e.target.value)}
                                        fullWidth
                                        helperText="Not the licence pool — seats are still assigned separately."
                                    />
                                    <TextField
                                        select
                                        label="School Organization Type"
                                        value={sc.institute_type}
                                        onChange={(e) => updateSchool(idx, 'institute_type', e.target.value)}
                                        fullWidth
                                    >
                                        {Object.values(ORGANIZATION_TYPE_CODE).map((code) => (
                                            <MenuItem key={code} value={code}>{ORGANIZATION_TYPE_CODE_VERBOSE[code]}</MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                */}
                                {/* School Primary Role / Activity hidden for a network's school card too. */}
                                {/*
                                <TextField
                                    select
                                    label="School Primary Role / Activity"
                                    value={sc.institute_primary_activities}
                                    onChange={(e) => updateSchool(idx, 'institute_primary_activities', e.target.value)}
                                    fullWidth sx={{ mb: 2 }}
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => selected
                                            .map((code) => PRIMARY_ACTIVITY_CODE_VERBOSE[code])
                                            .join(', '),
                                    }}
                                >
                                    {Object.values(PRIMARY_ACTIVITY_CODE).map((code) => (
                                        <MenuItem key={code} value={code}>
                                            <Checkbox checked={sc.institute_primary_activities.indexOf(code) > -1} />
                                            <ListItemText primary={PRIMARY_ACTIVITY_CODE_VERBOSE[code]} />
                                        </MenuItem>
                                    ))}
                                </TextField>
                                */}
                                <TextField
                                    label="School Website (optional)"
                                    value={sc.institute_website}
                                    onChange={(e) => updateSchool(idx, 'institute_website', e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    autoComplete="url"
                                />
                                {/* Additional Capabilities hidden for a network's school card too. */}
                                {/*
                                <TextField
                                    select
                                    label="Additional Capabilities"
                                    value={sc.institute_additional_capabilities}
                                    onChange={(e) => updateSchool(idx, 'institute_additional_capabilities', e.target.value)}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    SelectProps={{
                                        multiple: true,
                                        renderValue: (selected) => selected
                                            .map((code) => ADDITIONAL_CAPABILITY_CODE_VERBOSE[code])
                                            .join(', '),
                                    }}
                                >
                                    {Object.values(ADDITIONAL_CAPABILITY_CODE).map((code) => (
                                        <MenuItem key={code} value={code}>
                                            <Checkbox checked={sc.institute_additional_capabilities.indexOf(code) > -1} />
                                            <ListItemText primary={ADDITIONAL_CAPABILITY_CODE_VERBOSE[code]} />
                                        </MenuItem>
                                    ))}
                                </TextField>
                                */}

                                {/*
                                  * Combined address field hidden here too — Line 1
                                  * below is the real, required address.
                                  */}
                                {/*
                                <TextField
                                    label="School Address"
                                    value={sc.org_address}
                                    onChange={(e) => updateSchool(idx, 'org_address', e.target.value)}
                                    error={!!(schoolErrors[idx] || {}).org_address}
                                    helperText={(schoolErrors[idx] || {}).org_address}
                                    fullWidth sx={{ mb: 2 }}
                                />
                                */}
                                <TextField
                                    label="School Address — Line 1"
                                    value={sc.institute_address_line1}
                                    onChange={(e) => updateSchool(idx, 'institute_address_line1', e.target.value)}
                                    error={!!(schoolErrors[idx] || {}).institute_address_line1}
                                    helperText={(schoolErrors[idx] || {}).institute_address_line1}
                                    fullWidth sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="School Address — Line 2 (optional)"
                                    value={sc.institute_address_line2}
                                    onChange={(e) => updateSchool(idx, 'institute_address_line2', e.target.value)}
                                    fullWidth sx={{ mb: 2 }}
                                />
                                {/* Searchable — a plain select can't be typed into. */}
                                <Autocomplete
                                    options={getCountries()}
                                    getOptionLabel={(option) => option.label || ''}
                                    isOptionEqualToValue={(option, value) => option.code === value?.code}
                                    value={getCountries().find((c) => c.code === sc.org_country) || null}
                                    onChange={(event, newValue) => {
                                        updateSchool(idx, 'org_country', newValue?.code || '');
                                        // The state list is keyed by country — a stale code
                                        // from the previous country would point at the wrong state.
                                        updateSchool(idx, 'institute_state', '');
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Country"
                                            variant="outlined"
                                            error={!!(schoolErrors[idx] || {}).org_country}
                                            helperText={(schoolErrors[idx] || {}).org_country}
                                        />
                                    )}
                                    fullWidth
                                    sx={{ mb: 2 }}
                                />
                                {/* State/province, below Country since its options depend on
                                    which country is picked. Full width so a long state/province
                                    name doesn't get cropped. */}
                                {(() => {
                                    const statesForCountry = getStatesByCountry(sc.org_country);
                                    return (
                                        <Autocomplete
                                            options={statesForCountry}
                                            getOptionLabel={(option) => option.name || ''}
                                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                                            value={statesForCountry.find((s) => s.code === sc.institute_state) || null}
                                            onChange={(event, newValue) => {
                                                updateSchool(idx, 'institute_state', newValue ? newValue.code : '');
                                            }}
                                            disabled={!sc.org_country || statesForCountry.length === 0}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="School State / Province / Region"
                                                    variant="outlined"
                                                    placeholder={
                                                        !sc.org_country
                                                            ? 'Select country first'
                                                            : statesForCountry.length === 0 ? 'N/A' : 'Select state'
                                                    }
                                                />
                                            )}
                                            fullWidth
                                            sx={{ mb: 2 }}
                                        />
                                    );
                                })()}
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        label="City"
                                        value={sc.org_city}
                                        onChange={(e) => updateSchool(idx, 'org_city', e.target.value)}
                                        error={!!(schoolErrors[idx] || {}).org_city}
                                        helperText={(schoolErrors[idx] || {}).org_city}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Postal Code"
                                        value={sc.postal_code}
                                        onChange={(e) => updateSchool(idx, 'postal_code', e.target.value)}
                                        error={!!(schoolErrors[idx] || {}).postal_code}
                                        helperText={(schoolErrors[idx] || {}).postal_code}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        ))}

                        {/* Slide 41's notice, verbatim. */}
                        <FormNotice sx={{ mb: 1.5 }}>
                            To add another school, close this and use <strong>+ Add a school</strong> on
                            the network row. Schools can&apos;t be removed here — contact support if a
                            school has left the network.
                        </FormNotice>
                    </Box>
                )}

                {/* Slides 39 and 41 carry the same programme notice as the create form. */}
                <FormNotice sx={{ mb: 1.5 }}>
                    No program is linked automatically. After creating the {isNetwork ? 'network' : 'school'},
                    use <strong>Link Programs</strong> to give it access to the Child Online Safety
                    Assessment or any other program.
                </FormNotice>

            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none', borderRadius: '50px', px: 4, fontWeight: 700,
                        borderColor: 'var(--brand-border, #D1D5DB)',
                        color: 'var(--brand-text-muted, #6B7280)',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    // Enabled only when the account and every school in it are valid.
                    // Not until the stored school fields are in — saving the blanks
                    // would clear them.
                    disabled={loading || instituteLoading || !isFormValid}
                    variant="contained"
                    sx={{
                        textTransform: 'none', borderRadius: '50px', px: 4, fontWeight: 700,
                        backgroundColor: ORANGE, '&:hover': { backgroundColor: '#c9541e' },
                        '&.Mui-disabled': {
                            backgroundColor: 'var(--brand-border, #E5E7EB)',
                            color: 'var(--brand-text-muted, #9CA3AF)',
                        },
                    }}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>

        {/*
          * Slide 39 asks for an extra confirmation when the contact email —
          * i.e. who owns the workspace — is about to change. It never drew one,
          * so this is deliberately plain: name both addresses, say what happens,
          * and make the person choose.
          */}
        <Dialog
            open={!!pendingOwnershipChange}
            onClose={() => setPendingOwnershipChange(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '15px' } }}
        >
            <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pb: 0.5 }}>
                Change the workspace contact?
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 14, color: 'var(--brand-text-muted, #6B7280)', mb: 2, textAlign: 'center' }}>
                    This moves ownership of
                    {' '}<strong>{client?.org_name}</strong>{' '}
                    to a different person.
                </Typography>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
                    p: 2, mb: 2, fontSize: 13.5,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                        <span style={{ color: 'var(--brand-text-muted, #9CA3AF)' }}>Currently</span>
                        <span style={{ fontWeight: 600, wordBreak: 'break-word', textAlign: 'right' }}>
                            {pendingOwnershipChange?.from}
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <span style={{ color: 'var(--brand-text-muted, #9CA3AF)' }}>Changing to</span>
                        <span style={{ fontWeight: 700, color: ORANGE, wordBreak: 'break-word', textAlign: 'right' }}>
                            {pendingOwnershipChange?.to}
                        </span>
                    </Box>
                </Box>
                <Box sx={{
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '8px',
                    p: 1.5, fontSize: 13, color: 'var(--brand-text-muted, #6B7280)',
                }}>
                    {isAccountPublished(client?.lifecycle_status)
                        ? 'This workspace is already published, so the new contact will need an activation email before they can sign in. Use Resend activation email on the row once you have saved.'
                        : 'Nothing is sent yet. The new contact receives the activation email when you publish this workspace.'}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => setPendingOwnershipChange(null)}
                    sx={{
                        textTransform: 'none', borderRadius: '50px', px: 4, fontWeight: 700,
                        borderColor: 'var(--brand-border, #D1D5DB)',
                        color: 'var(--brand-text-muted, #6B7280)',
                    }}
                >
                    Keep current contact
                </Button>
                <Button
                    variant="contained"
                    disabled={loading}
                    onClick={() => { setPendingOwnershipChange(null); commit(); }}
                    sx={{
                        textTransform: 'none', borderRadius: '50px', px: 4, fontWeight: 700,
                        backgroundColor: ORANGE, '&:hover': { backgroundColor: '#c9541e' },
                    }}
                >
                    Change contact
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default EditClientDialog;
